import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, AuthTokens, PlanType } from '../types';
import { TOKEN_STORAGE_KEY } from '../config/constants';

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isMfaEnabled: boolean | null;
  isSessionExpired: boolean;

  // Actions
  setAuth: (user: User, tokens: AuthTokens) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (updates: Partial<User>) => void;
  updatePlan: (plan: PlanType) => void;
  setTokens: (tokens: AuthTokens) => void;
  setMfaEnabled: (enabled: boolean) => void;
  setSessionExpired: (expired: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      accessToken: null,
      idToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      isMfaEnabled: null,
      isSessionExpired: false,

      // Actions
      setAuth: (user, tokens) =>
        set({
          user,
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: tokens.refresh_token,
          isAuthenticated: true,
          isLoading: false,
        }),

      clearAuth: () =>
        set({
          user: null,
          accessToken: null,
          idToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          isMfaEnabled: null,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      updatePlan: (plan) =>
        set((state) => ({
          user: state.user ? { ...state.user, plan } : null,
        })),

      setTokens: (tokens) =>
        set({
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: tokens.refresh_token,
          isSessionExpired: false,  // 連鎖ロック解消: refresh成功時にモーダルを自動クリア
        }),

      setMfaEnabled: (enabled) => set({ isMfaEnabled: enabled }),

      setSessionExpired: (expired) => set({ isSessionExpired: expired }),
    }),
    {
      name: TOKEN_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        idToken: state.idToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        isMfaEnabled: state.isMfaEnabled,
        isSessionExpired: state.isSessionExpired,
      }),
    }
  )
);

// Selectors
export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectIsAdmin = (state: AuthState) => state.user?.role === 'admin';
export const selectUserPlan = (state: AuthState) => state.user?.plan || 'free';
